/**
 * Parse and normalize LLM responses into ClassifiedIntent objects.
 *
 * Handles valid JSON, malformed JSON with fallback extraction,
 * and complete failures with error recovery.
 *
 * @module gibbs/text-to-action/parseResponse
 */

import { classifyIntent } from './classifyIntent';

/**
 * Parse an LLM response into a classified intent.
 *
 * @param {import('./llmClient.js').LLMResponse} llmResponse
 * @returns {{ classified: Object, parseErrors: string[] }}
 */
export function parseResponse(llmResponse) {
    const parseErrors = [];

    // Handle request-level errors
    if (llmResponse.error) {
        parseErrors.push(`LLM error: ${llmResponse.error}`);
        return {
            classified: {
                intent: 'chat',
                message: 'I encountered an error processing your request. Could you try rephrasing?',
            },
            parseErrors,
        };
    }

    // If already parsed as JSON, use it directly
    if (llmResponse.parsed) {
        const classified = classifyIntent(JSON.stringify(llmResponse.parsed));
        return { classified, parseErrors };
    }

    // Try to parse the raw content
    const content = llmResponse.content || '';

    if (!content.trim()) {
        parseErrors.push('Empty response from LLM');
        return {
            classified: {
                intent: 'chat',
                message: 'I didn\'t generate a response. Could you try again?',
            },
            parseErrors,
        };
    }

    // Use classifyIntent which has robust fallback parsing
    const classified = classifyIntent(content);

    // Check if classifyIntent had to use fallback
    if (classified.wasFallback) {
        parseErrors.push(`Used fallback parsing: ${classified.wasFallbackReason || 'malformed JSON'}`);
        delete classified.wasFallback;
        delete classified.wasFallbackReason;
    }

    return { classified, parseErrors };
}
